import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * One piece of evidence a report points at. `ref` is interpreted per `kind`:
 * a SHA for `commit`, a PR url/number for `pr`, a repo-relative path for
 * `path`, an absolute url for `url`, an issue identifier for `issue`.
 *
 * @since 1.0.0
 */
export const ReportReference = Schema.Struct({
  kind: Schema.Literal("commit", "pr", "path", "url", "issue"),
  ref: Schema.String,
  note: Schema.optional(Schema.String),
});
export type ReportReference = Schema.Schema.Type<typeof ReportReference>;

/**
 * A single problem a verifier run called out.
 *
 * @since 1.0.0
 */
export const ReportFinding = Schema.Struct({
  title: Schema.String,
  severity: Schema.String,
  detail: Schema.String,
});
export type ReportFinding = Schema.Schema.Type<typeof ReportFinding>;

/**
 * A verifier run's judgement: did the work pass, with optional numeric metrics
 * and the findings behind the call. Present only on verifier reports.
 *
 * @since 1.0.0
 */
export const ReportVerdict = Schema.Struct({
  passed: Schema.Boolean,
  metrics: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Number })),
  findings: Schema.optional(Schema.Array(ReportFinding)),
});
export type ReportVerdict = Schema.Schema.Type<typeof ReportVerdict>;

/**
 * Report model — a run's textual result plus its references (OL-31).
 *
 * The references field is called `refs`, not `references`: the latter is a
 * PostgreSQL reserved word. Reports are append-only, so there is a `createdAt`
 * and deliberately no `updatedAt` — a second report from the same run is a
 * second row.
 *
 * `customerId` is a plain string with no foreign key; `customers` lives in the
 * `public` schema and this table lives in `orcaloops`.
 *
 * @since 1.0.0
 */
export class Report extends Model.Class<Report>("Report")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,
  runId: Schema.String,

  body: Schema.String,

  refs: Schema.Array(ReportReference),

  verdict: Schema.NullOr(ReportVerdict),

  createdAt: Model.DateTimeInsertFromDate,
}) {}
